import API from "./api";
import { SimplePoster, Poster } from "@/models/poster";
import sesionService from "./sesion";
import pick from "lodash/pick";
import axios, { CancelTokenSource } from "axios";

export type PosterType = "user" | "eval" | "all";
export const posterTypes: PosterType[] = ["user", "eval"];

export interface PosterUploadsForm {
  id_poster: string | number;
  file: File;
  url_video: string;
}

export interface EvalPreinscripcionForm {
  id_evaluador: string | number;
  id_poster: string | number;
  preaceptado: boolean;
  tipo_aceptacion: string | null;
  observacion: string | null;
  estado?: string;
}
export interface EvaluacionForm {
  id_evaluador_poster: number;
  calificacion: number;
}

export interface PosterUploadsResult {
  id_poster: string | number;
  file: string;
  url_video: string;
}

export interface PosterPDFDetails {
  // resumen: string
  // problema_inv: string
  // ref_teorico: string
  conclusiones: string;
  // referencias: string
  titulo: string;
  introduccion: string;
  detalle_tipo: string;
  objetivo_general: string;
  // objetivo_especifico: string
  metodologia: string;
  resultados: string;
  palabras_claves: string;
  tipo: string;
  evento: string;
  autores: string;
  id_participante: string | number;
}

const posterRequests: Record<PosterType, string> = {
  user: "/poster",
  eval: "/evaluador/posters",
  all: "/poster/revisor",
};

const posterDetailsRequest: Partial<Record<
  PosterType,
  (id: string | number) => string
>> = {
  user: (id) => `/poster/${id}`,
  eval: (id) => `/evaluador/poster/${id}`,
  all: (id) => `/poster/revisor/${id}`,
};

export const posterService = {
  async get(type: PosterType): Promise<SimplePoster[]> {
    const { data } = await API.getRequestWithAuth(
      posterRequests[type],
      sesionService.token
    );
    return data;
  },

  async getPoster(
    id_poster: string | number,
    type: PosterType
  ): Promise<Poster> {
    const request = posterDetailsRequest[type];
    if (!request) {
      throw new Error(`No details request for type '${type}'`);
    }

    const { data } = await API.getRequestWithAuth(
      request(id_poster),
      sesionService.token
    );
    return data;
  },

  async addPoster(form: Poster): Promise<Poster> {
    const { data } = await API.postRequestWithAuth(
      "/poster/register",
      sesionService.token,
      form
    );
    return data;
  },

  uploadCancel: null as CancelTokenSource | null,
  async posterUploads(form: PosterUploadsForm): Promise<PosterUploadsResult> {
    const formData = new FormData();
    formData.append("id_poster", String(form.id_poster));
    formData.append("file", form.file);
    formData.append("url_video", form.url_video);

    const source = axios.CancelToken.source();
    const p = API.postRequestWithAuth(
      "/file/upload",
      sesionService.token,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        cancelToken: source.token,
      }
    );

    this.uploadCancel = source;
    p.finally(() => (this.uploadCancel = null));

    const { data } = await p;
    return data;
  },

  async updatePoster(form: Poster): Promise<Poster> {
    const { data } = await API.postRequestWithAuth(
      "/poster/update",
      sesionService.token,
      form
    );
    return data;
  },

  async evalPreinscripcionPoster(form: EvalPreinscripcionForm): Promise<any> {
    const { data } = await API.postRequestWithAuth(
      "/poster/evalpreinscripcion",
      sesionService.token,
      form
    );
    return data;
  },

  async evaluacionPoster(form: EvaluacionForm): Promise<any> {
    const { data } = await API.postRequestWithAuth(
      "/poster/evaluacionposter",
      sesionService.token,
      form
    );
    return data;
  },

  pdf(form: PosterPDFDetails): Promise<any> {
    return API.postRequestWithAuth("/pdf", sesionService.token, form, {
      method: "POST",
      responseType: "blob",
    }) as any;
  },

  pdfFromPoster(poster: Poster) {
    return this.pdf(
      pick<any, keyof PosterPDFDetails>(poster, [
        // 'resumen',
        // 'problema_inv',
        // 'ref_teorico',
        "conclusiones",
        // 'referencias',
        "titulo",
        "introduccion",
        "detalle_tipo",
        "objetivo_general",
        // 'objetivo_especifico',
        "metodologia",
        "resultados",
        "palabras_claves",
        "tipo",
        "evento",
        "autores",
        "id_participante",
      ]) as PosterPDFDetails
    );
  },

  async fetchFile(poster: SimplePoster): Promise<Blob> {
    const response = await API.getRequestWithAuth(
      `/poster/${poster.id_poster}/file`,
      sesionService.token,
      { responseType: "blob" }
    );
    return response.data as Blob;
  },
};

export default posterService;
